import React, { useEffect, useState } from 'react';
import './MarketingPage.css';
import { FaGraduationCap, FaBook, FaChartLine, FaLightbulb, FaLock, FaVolumeUp, FaRegCheckCircle, FaSave, FaChalkboardTeacher, FaCubes } from 'react-icons/fa';

function MarketingPage({ onGetStarted }) {
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll animation effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Find all elements with the fade-in-up class
      const fadeElements = document.querySelectorAll('.fade-in-up');
      fadeElements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('is-visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="marketing-container">
      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="hero-particles">
          {/* Background particles or animation can be added here */}
        </div>
        <div className="hero-content">
          <div className="hero-logo">GenAI Tutor</div>
          <div className="hero-tagline">
            Revolutionize learning with AI-powered education that adapts to your needs. From grade school to advanced topics, we provide personalized learning experiences.
          </div>
          <button className="cta-button" onClick={onGetStarted}>
            Get Started Now
          </button>
        </div>
      </section>

      {/* Badges Section */}
      <div className="badges-container">
        <div className="badge">
          <div className="badge-icon"><FaGraduationCap /></div>
          <div className="badge-text">All Grades (1-12)</div>
        </div>
        <div className="badge">
          <div className="badge-icon"><FaBook /></div>
          <div className="badge-text">AP Courses</div>
        </div>
        <div className="badge">
          <div className="badge-icon"><FaLightbulb /></div>
          <div className="badge-text">Real-life Examples</div>
        </div>
        <div className="badge">
          <div className="badge-icon"><FaChartLine /></div>
          <div className="badge-text">Dynamic Visualization</div>
        </div>
      </div>

      {/* Key Features Section */}
      <section className="features-container">
        <h2 className="section-title fade-in-up is-visible">Why Choose GenAI Tutor?</h2>
        <div className="features-grid">
          <div className="feature-card fade-in-up is-visible">
            <div className="feature-icon"><FaGraduationCap /></div>
            <h3 className="feature-title">Comprehensive Coverage</h3>
            <p className="feature-description">
              From elementary school to high school, including AP courses with detailed step-by-step instructions tailored to your curriculum.
            </p>
          </div>

          <div className="feature-card fade-in-up is-visible">
            <div className="feature-icon"><FaLightbulb /></div>
            <h3 className="feature-title">Dual Explanation Approach</h3>
            <p className="feature-description">
              Quick overviews for rapid understanding, plus in-depth explanations with real-life examples that traditional platforms don't offer.
            </p>
          </div>

          <div className="feature-card fade-in-up is-visible">
            <div className="feature-icon"><FaChartLine /></div>
            <h3 className="feature-title">Progressive Practice</h3>
            <p className="feature-description">
              Begin with easier questions to build confidence, then challenge yourself with more difficult problems to truly master each topic.
            </p>
          </div>

          <div className="feature-card fade-in-up is-visible">
            <div className="feature-icon"><FaSave /></div>
            <h3 className="feature-title">Progress Tracking</h3>
            <p className="feature-description">
              All your answers are saved to your account, allowing you to pause and resume your learning journey at any time.
            </p>
          </div>

          <div className="feature-card fade-in-up is-visible">
            <div className="feature-icon"><FaLock /></div>
            <h3 className="feature-title">Secure Answer Access</h3>
            <p className="feature-description">
              Built-in security features require a PIN to view answers, preventing unauthorized access to solutions.
            </p>
          </div>

          <div className="feature-card fade-in-up is-visible">
            <div className="feature-icon"><FaCubes /></div>
            <h3 className="feature-title">Interactive Visualizations</h3>
            <p className="feature-description">
              Generate dynamic charts, diagrams, and 3D models that help you visualize and understand complex concepts.
            </p>
          </div>
        </div>
      </section>

      {/* Visual Features Section */}
      <section className="visual-features">
        <div className="visual-container">
          <h2 className="section-title fade-in-up">See What Sets Us Apart</h2>
          
          {/* Feature Row 1 */}
          <div className="visual-row fade-in-up">
            <div className="visual-content">
              <h3 className="visual-title">Personalized Learning Experience</h3>
              <p className="visual-description">
                Our AI-powered tutor adapts to your learning style and pace, providing explanations that make sense to you specifically.
              </p>
              <ul className="feature-list">
                <li>Tailored explanations based on your grade level</li>
                <li>Real-life examples that make concepts relatable</li>
                <li>Step-by-step breakdowns of complex problems</li>
                <li>Voice narration for auditory learners</li>
              </ul>
            </div>
            <div className="visual-image">
              <div className="image-placeholder">
                <img src="/ai.avif" alt="AI-powered learning" />
              </div>
            </div>
          </div>
          
          {/* Feature Row 2 */}
          <div className="visual-row row-reverse fade-in-up">
            <div className="visual-content">
              <h3 className="visual-title">AP Course Mastery</h3>
              <p className="visual-description">
                Excel in your Advanced Placement courses with comprehensive coverage of all AP subjects and detailed explanations of complex topics.
              </p>
              <ul className="feature-list">
                <li>Detailed coverage of all AP curriculum topics</li>
                <li>Practice questions similar to actual AP exams</li>
                <li>Step-by-step solutions for difficult problems</li>
                <li>Concept connections across subjects</li>
              </ul>
            </div>
            <div className="visual-image">
              <div className="image-placeholder">
                <FaChalkboardTeacher style={{ fontSize: '120px', color: '#4285f4' }} />
              </div>
            </div>
          </div>
          
          {/* Feature Row 3 */}
          <div className="visual-row fade-in-up">
            <div className="visual-content">
              <h3 className="visual-title">Visualize Complex Concepts</h3>
              <p className="visual-description">
                Our platform can generate dynamic graphs, charts, diagrams and 3D models to help you understand difficult concepts through visual learning.
              </p>
              <ul className="feature-list">
                <li>Interactive 3D models for scientific concepts</li>
                <li>Dynamic graph generation for mathematical functions</li>
                <li>Visual diagrams to illustrate abstract ideas</li>
                <li>Compare and contrast visual explanations</li>
              </ul>
            </div>
            <div className="visual-image">
              <div className="image-placeholder">
                <FaCubes style={{ fontSize: '120px', color: '#4285f4' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audio Feature Highlight */}
      <section className="cta-section">
        <div className="cta-container fade-in-up">
          <div className="feature-icon" style={{ margin: '0 auto 20px', background: 'rgba(255,255,255,0.1)' }}>
            <FaVolumeUp style={{ color: 'white' }} />
          </div>
          <h2 className="cta-title">Learning for Every Sense</h2>
          <p className="cta-description">
            Don't feel like reading? Our audio feature can speak the content aloud, perfect for auditory learners or when you're on the go.
          </p>
          <div className="benefit-cards">
            <div className="benefit-card">
              <h4 className="benefit-title">Multitasking-Friendly</h4>
              <p>Learn while doing other activities</p>
            </div>
            <div className="benefit-card">
              <h4 className="benefit-title">Perfect for Auditory Learners</h4>
              <p>Hear explanations clearly articulated</p>
            </div>
            <div className="benefit-card">
              <h4 className="benefit-title">Reduce Eye Strain</h4>
              <p>Give your eyes a break while continuing to learn</p>
            </div>
          </div>
          <button className="cta-button" onClick={onGetStarted}>
            Try Audio Learning Now
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <h2 className="section-title fade-in-up">What Our Students Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card fade-in-up">
              <p className="testimonial-text">
                "GenAI Tutor helped me understand calculus concepts that I struggled with for months. The real-life examples made everything click!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">M</div>
                <div className="author-info">
                  <h4>Maya S.</h4>
                  <p>AP Calculus Student</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card fade-in-up">
              <p className="testimonial-text">
                "I love being able to practice with easy questions first, then gradually move to harder ones. It's built my confidence in physics tremendously."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">J</div>
                <div className="author-info">
                  <h4>James K.</h4>
                  <p>High School Senior</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card fade-in-up">
              <p className="testimonial-text">
                "As a parent, I appreciate the PIN security for answers. It ensures my daughter works through problems herself before checking solutions."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">L</div>
                <div className="author-info">
                  <h4>Lisa M.</h4>
                  <p>Parent</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section" style={{ background: 'linear-gradient(90deg, #34a853 0%, #4285f4 100%)' }}>
        <div className="cta-container fade-in-up">
          <h2 className="cta-title">Ready to Transform Your Learning Experience?</h2>
          <p className="cta-description">
            Join thousands of students who have already discovered the power of personalized AI tutoring. Start your journey today!
          </p>
          <button className="cta-button" style={{ background: 'white', color: '#4285f4' }} onClick={onGetStarted}>
            Sign In Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="marketing-footer">
        <div className="footer-content">
          <div className="footer-links">
            <a href="#" className="footer-link">About Us</a>
            <a href="#" className="footer-link">Privacy Policy</a>
            <a href="#" className="footer-link">Terms of Service</a>
            <a href="#" className="footer-link">Contact</a>
            <a href="#" className="footer-link">Help Center</a>
          </div>
          <p className="copyright">© {new Date().getFullYear()} GenAI Tutor. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default MarketingPage;